/**
 * PublicChecklistUpload — the no-login page a direct subscriber opens
 * from the link the reviewer shared over WhatsApp / Email.
 *
 * Route: /checklist-upload/:token (public — NO auth, NO tenant chrome).
 * BE: GET /api/v1/public/checklist/{token} (data-minimised payload;
 * the FIRST open starts the configurable validity countdown) and
 * POST /api/v1/public/checklist/{token}/upload (multipart, optional
 * item_id ties the file to a checklist row).
 *
 * The page uses plain axios (no auth interceptors — there is no
 * account) and renders a friendly expired state when the token dies.
 */
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Alert,
  Button,
  Card,
  List,
  Progress,
  Result,
  Skeleton,
  Space,
  Tag,
  Typography,
  Upload,
  message as antMessage,
} from "antd";
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloudUploadOutlined,
  FileProtectOutlined,
} from "@ant-design/icons";
import axios from "axios";

const { Title, Paragraph, Text } = Typography;

const BASE = process.env.REACT_APP_API_BASE_URL || "";

export default function PublicChecklistUpload() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dead, setDead] = useState(false);
  const [uploadingItem, setUploadingItem] = useState(null);
  // Server-computed seconds until the link dies (first-open window /
  // absolute cap, whichever is sooner). Ticks down locally; reaching
  // zero flips the page into the expired state without a reload.
  const [secondsLeft, setSecondsLeft] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await axios.get(
        `${BASE}/api/v1/public/checklist/${encodeURIComponent(token)}`,
      );
      setData(resp.data || null);
      if (typeof resp.data?.link_seconds_remaining === "number") {
        setSecondsLeft(resp.data.link_seconds_remaining);
      }
      setDead(false);
    } catch {
      setDead(true);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  // 1-second tick. At zero the link is gone server-side too (the BE
  // enforces the same window on every read and upload).
  useEffect(() => {
    if (secondsLeft === null || dead) return undefined;
    if (secondsLeft <= 0) {
      setDead(true);
      return undefined;
    }
    const t = setInterval(
      () => setSecondsLeft((v) => (v === null ? v : v - 1)),
      1000,
    );
    return () => clearInterval(t);
  }, [secondsLeft === null, secondsLeft <= 0, dead]); // eslint-disable-line react-hooks/exhaustive-deps

  const fmtCountdown = (total) => {
    if (total === null) return null;
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const sec = total % 60;
    const mm = String(m).padStart(2, "0");
    const ss = String(sec).padStart(2, "0");
    return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
  };

  const uploadFor = async (file, itemId) => {
    setUploadingItem(itemId || "__general__");
    try {
      const payload = new FormData();
      payload.append("file", file, file.name);
      if (itemId) payload.append("item_id", itemId);
      await axios.post(
        `${BASE}/api/v1/public/checklist/${encodeURIComponent(token)}/upload`,
        payload,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      antMessage.success(`${file.name} uploaded`);
      await load();
    } catch (e) {
      const detail =
        e?.response?.data?.detail || "Upload failed. Please try again.";
      antMessage.error(detail);
      if (e?.response?.status === 404) setDead(true);
    } finally {
      setUploadingItem(null);
    }
    return false; // we handled it — stop antd's default upload action
  };

  if (loading && !data) {
    return (
      <div className="min-h-screen bg-slate-50 p-6">
        <div className="max-w-2xl mx-auto">
          <Card>
            <Skeleton active paragraph={{ rows: 6 }} />
          </Card>
        </div>
      </div>
    );
  }

  if (dead || !data) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <Card style={{ maxWidth: 480 }}>
          <Result
            status="warning"
            title="This link is no longer available"
            subTitle="The upload link has expired or was replaced. Please ask your consultant to send a fresh link."
          />
        </Card>
      </div>
    );
  }

  const items = data.items || [];
  const provided = items.filter(
    (i) => i.status === "provided" || i.status === "not_applicable",
  ).length;

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-6">
          <FileProtectOutlined style={{ fontSize: 40, color: "#1e3a8a" }} />
          <Title level={3} className="mt-2 mb-1!">
            Documents required for your notice response
          </Title>
          <Paragraph className="text-slate-500 mb-0!">
            {data.portal_label ? `${data.portal_label} · ` : ""}
            {data.notice_label || "Tax notice"}
          </Paragraph>
        </div>

        <Card className="mb-4">
          <Space className="mb-2" size="middle" wrap>
            <Text strong>Progress</Text>
            <Tag color="geekblue">
              {provided}/{items.length} provided
            </Tag>
            {secondsLeft !== null && (
              <Tag
                icon={<ClockCircleOutlined />}
                color={secondsLeft <= 300 ? "red" : "gold"}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  fontVariantNumeric: "tabular-nums",
                  fontWeight: 600,
                }}
              >
                Link expires in {fmtCountdown(secondsLeft)}
              </Tag>
            )}
          </Space>
          <Progress
            percent={items.length ? Math.round((provided / items.length) * 100) : 0}
          />
          <Alert
            className="mt-3"
            type="info"
            showIcon
            message="Upload each document against its item below. Files reach your consultant securely."
          />
        </Card>

        <Card title="Required documents">
          <List
            itemLayout="vertical"
            dataSource={items}
            renderItem={(item) => {
              const done =
                item.status === "provided" || item.status === "not_applicable";
              return (
                <List.Item key={item.item_id}>
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div style={{ flex: 1, minWidth: 220 }}>
                      <Space size={8}>
                        {done ? (
                          <CheckCircleOutlined style={{ color: "#16a34a" }} />
                        ) : null}
                        <Text strong>{item.title}</Text>
                        {item.mandatory && !done && (
                          <Tag color="red" className="m-0!">
                            Required
                          </Tag>
                        )}
                        {done && (
                          <Tag color="green" className="m-0!">
                            Provided
                          </Tag>
                        )}
                      </Space>
                      {item.why_needed && (
                        <Paragraph className="text-slate-500 text-sm mt-1 mb-0!">
                          {item.why_needed}
                        </Paragraph>
                      )}
                    </div>
                    <Upload
                      showUploadList={false}
                      beforeUpload={(file) => uploadFor(file, item.item_id)}
                      disabled={uploadingItem !== null}
                    >
                      <Button
                        icon={<CloudUploadOutlined />}
                        loading={uploadingItem === item.item_id}
                      >
                        {done ? "Upload another" : "Upload"}
                      </Button>
                    </Upload>
                  </div>
                </List.Item>
              );
            }}
          />

          <div className="mt-4 pt-4 border-t border-slate-100">
            <Space direction="vertical" size={4} style={{ width: "100%" }}>
              <Text strong>Any other document</Text>
              <Text type="secondary" className="text-sm">
                Something relevant that is not on the list above.
              </Text>
              <Upload
                showUploadList={false}
                beforeUpload={(file) => uploadFor(file, null)}
                disabled={uploadingItem !== null}
              >
                <Button
                  icon={<CloudUploadOutlined />}
                  loading={uploadingItem === "__general__"}
                >
                  Upload additional document
                </Button>
              </Upload>
            </Space>
          </div>
        </Card>

        <Paragraph className="text-center text-slate-400 text-xs mt-4">
          This secure link is time-limited and personal to your notice.
        </Paragraph>
      </div>
    </div>
  );
}
